import { ReferralsRequest } from 'v3/Models/ReferralRequest';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { first } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import { id } from 'date-fns/locale';
import { name } from 'faker';

export const DEFAULT_REFERRAL_REQUEST: ReferralsRequest = {
  id: '',
  skipValidation: true,
  referralSourceId: '',
  referralDate: new Date(),
  firstName: '',
  lastName: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  email: '',
  specialInstructions: '',
  referralRequestDraftDocuments: [],
  dateOfBirth: new Date(),
  dueDate: new Date(),
  referralNumber: '',
  dateOfInjury: new Date(),
  dateOfRequest: new Date(),
  dateOfExam: new Date(),
  employerName: '',
  employerLocation: '',
  employerZipCode: '',
  medications: first([]),
  tat: first([]),
  tatPriority: first([]),
  claimNumber: '',
  jurisditionalMatchRequired: first([]),
  benefitState: first([]),
  requestedReviewerSpecialty: first([]),
  numberOfReviews: undefined,
  requestedReviewerSpecialties: [],
  service: first([]),
  subService: first([]),
  attendingProviders: [
    {
      apName: '',
      apSpecialty: { value: '', displayName: '' },
      apPhoneNumber: '',
      apEmail: '',
    },
  ],
  apContactPreference: '',
  additionalQuestions: [
    {
      value: '',
      id: uuidv4(),
      isSelected: true,
      isAdditional: true,
    },
  ],
};

type UseReferralRequestFormProps = {
  onSubmit: (values: ReferralsRequest) => void;
  referralRequest?: ReferralsRequest | null;
  isInitialLoad: boolean;
};

export const useReferralRequestForm = ({
  onSubmit,
  referralRequest,
  isInitialLoad,
}: UseReferralRequestFormProps) => {
  const formik = useFormik<ReferralsRequest>({
    enableReinitialize: true,
    initialValues: {
      ...DEFAULT_REFERRAL_REQUEST,
      ...referralRequest,
      jurisditionalMatchRequired: isInitialLoad
        ? undefined
        : referralRequest?.jurisditionalMatchRequired,
    },
    onSubmit: onSubmit,
    validateOnMount: true,
  });

  return formik;
};

const AddEditReferralRequestSections = {
  MedicalRecords: 'Medical Records',
  ClaimantInformation: 'Claimant Information',
  ClaimDetails: 'Claim Details',
  ReportDetails: 'Report Details',
  AttorneysInformation: 'Attorney Details',
  ReportQuestions: 'Report Questions',
};

type Values = Record<string, any>;

export const createReferralRequestSchema = (lob: LineOfBusinessEnum) => {
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp;
  const isIME = lob === LineOfBusinessEnum.IME;
  const isDisability = lob === LineOfBusinessEnum.Disability;
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview;
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy;
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview;

  return Yup.object().shape({
    specialInstructions: Yup.bool().meta({
      label: 'Comments',
      section: AddEditReferralRequestSections.MedicalRecords,
    }),
    firstName: Yup.string().required().meta({
      label: 'First Name',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: true /* All lobs */,
    }),
    lastName: Yup.string().required().meta({
      label: 'Last Name',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: true /* All lobs */,
    }),
    streetAddress: Yup.string().nullable().meta({
      label: 'Street Address',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: isIME,
    }),
    city: Yup.string().nullable().meta({
      label: 'City',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: isIME,
    }),
    zipCode: Yup.string().nullable().meta({
      label: 'Zip Code',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: isIME,
    }),
    phone: Yup.string().nullable().meta({
      label: 'Phone Number',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: !isIME,
    }),
    email: Yup.string().nullable().meta({
      label: 'Email',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: !isIME,
    }),
    state: Yup.object().nullable().meta({
      label: 'State',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: isIME,
    }),
    gender: Yup.object().required().meta({
      label: 'Gender',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: true /* All lobs */,
    }),
    apContactPreference: Yup.string().nullable().meta({
      label: 'AP Contact Preference',
      section: AddEditReferralRequestSections.ReportDetails,
      required: true /* All lobs */,
    }),
    attendingProviders: Yup.array()
      .of(
        Yup.object().shape({
          apName: Yup.string().required(),
          apSpecialty: Yup.string().required(),
          apPhoneNumber: Yup.string().required(),
          apEmail: Yup.string().required(),
        })
      )
      .meta({
        label: 'Attending Providers',
        section: AddEditReferralRequestSections.ReportDetails,
      }),
    dateOfBirth: Yup.date().required().meta({
      label: 'Date of Birth',
      section: AddEditReferralRequestSections.ClaimantInformation,
      required: true /* All lobs */,
    }),
    dueDate: Yup.date().nullable().meta({
      label: 'Due Date',
      section: AddEditReferralRequestSections.ReportDetails,
      required: true /* All lobs */,
    }),
    claimNumber: Yup.string().nullable().meta({
      label: 'Claim/Client Number',
      section: AddEditReferralRequestSections.ClaimDetails,
      required: true /* all lobs */,
    }),
    referralNumber: Yup.string().nullable().meta({
      label: 'Referral Number',
      section: AddEditReferralRequestSections.ClaimDetails,
    }),
    dateOfInjury: Yup.date()
      .nullable()
      .meta({
        label: 'Date of Injury',
        section: AddEditReferralRequestSections.ClaimDetails,
        required: isWorkersComp || isImpairmentRatingReview,
      }),
    dateOfRequest: Yup.date().nullable().meta({
      label: 'Date of Request',
      section: AddEditReferralRequestSections.ClaimDetails,
    }),
    dateOfExam: Yup.date().nullable().meta({
      label: 'Date of Exam',
      section: AddEditReferralRequestSections.ClaimDetails,
    }),
    employerName: Yup.string().nullable().meta({
      label: 'Employer Name',
      section: AddEditReferralRequestSections.ClaimDetails,
    }),
    employerLocation: Yup.object().nullable().meta({
      label: 'Employer Location',
      section: AddEditReferralRequestSections.ClaimDetails,
    }),
    employerZipCode: Yup.string().nullable().meta({
      label: 'Employer Zip Code',
      section: AddEditReferralRequestSections.ClaimDetails,
    }),
    medications: Yup.array()
      .of(
        Yup.object().shape({
          medicationName: Yup.string().required(),
          medicationDetails: Yup.string().required(),
        })
      )
      .meta({
        label: 'Medications',
        section: AddEditReferralRequestSections.ClaimDetails,
        required: isPharmacy,
      }),
    tat: Yup.object().nullable().meta({
      label: 'TAT',
      section: AddEditReferralRequestSections.ReportDetails,
    }),
    tatPriority: Yup.object().nullable().meta({
      label: 'TAT Priority',
      section: AddEditReferralRequestSections.ReportDetails,
    }),
    jurisditionalMatchRequired: Yup.boolean().nullable().meta({
      label: 'Jurisdictional Match Required',
      section: AddEditReferralRequestSections.ReportDetails,
      required: !isIME,
    }),
    benefitState: Yup.object()
      .meta({
        label: 'State of Jurisdiction',
        section: AddEditReferralRequestSections.ReportDetails,
        required: true /* All lobs */,
      }),
    requestedReviewerSpecialty: Yup.object().nullable().meta({
      label: 'Requested Reviewer Specialty',
      section: AddEditReferralRequestSections.ReportDetails,
    }),
    service: Yup.object().nullable().meta({
      label: 'Service',
      section: AddEditReferralRequestSections.ReportDetails,
      required: true /* All lobs */,
    }),
    subService: Yup.object().nullable().meta({
      label: 'Sub Service',
      section: AddEditReferralRequestSections.ReportDetails,
      required: true /* All lobs */,
    }),
    numberOfReviews: Yup.number()
      .nullable()
      .meta({
        label: 'Number of Reviews',
        section: AddEditReferralRequestSections.ReportDetails,
        required: isWorkersComp,
      })
      .when('jurisdionalMatchRequired', {
        is: true,
        then: Yup.object().meta({
          label: 'Number of Reviews',
          section: AddEditReferralRequestSections.ReportDetails,
          required: !isIME,
        }),
      })
      .when('jurisdionalMatchRequired', {
        is: false,
        then: Yup.object().meta({
          label: 'Number of Reviews',
          section: AddEditReferralRequestSections.ReportDetails,
          required: false,
        }),
      }),
    requestedReviewerSpecialties: Yup.array()
      .of(
        Yup.object().shape({
          specialty: Yup.string().required(),
          numberOfReviews: Yup.number().required(),
        })
      )
      .meta({
        label: 'Requested Reviewer Specialties',
        section: AddEditReferralRequestSections.ReportDetails,
      }),
    attorneys: Yup.array()
      .of(
        Yup.object().shape({
          attorneyName: Yup.string().required(),
          primaryContact: Yup.string().required(),
          additionalContact: Yup.string().required(),
        })
      )
      .meta({
        label: 'Attorneys',
        section: AddEditReferralRequestSections.AttorneysInformation,
      }),
    clientReportQuestions: Yup.array()
      .of(
        Yup.object().shape({
          value: Yup.string().nullable(),
          id: Yup.string().nullable(),
          isSelected: Yup.bool().nullable(),
        })
      )
      .meta({
        label: 'Report Questions',
        section: AddEditReferralRequestSections.ReportQuestions,
      }),
    additionalQuestions: Yup.array()
      .of(
        Yup.object().shape({
          value: Yup.string().nullable(),
          id: Yup.string().nullable(),
          isSelected: Yup.bool().nullable(),
        })
      )
      .meta({
        label: 'Additional Report Questions',
        section: AddEditReferralRequestSections.ReportQuestions,
      }),
  });
};
